/* eslint-disable react-hooks/exhaustive-deps */
import {
  Card,
  Col,
  Divider,
  Form,
  InputNumber,
  message,
  Row,
  Select,
  SelectProps,
  Space,
  Spin,
} from "@pankod/refine-antd";
import { useQuery } from "@pankod/refine-core";
import { CustomUploader } from "components/extra/custom-uploader";
import {
  INextShootSchedule,
  NextShootCard,
} from "components/extra/next-shoot-card";
import { ShootExecutionInfoCard } from "components/extra/shootexecution-info-card";
import dayjs, { Dayjs } from "dayjs";
import { ShootExecutionStatus } from "enums";
import { ICampaignShoot, IScheduleWindow } from "interfaces";
import { useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import { getScheduleWindow } from "services/shoot-execution";
import { shootExecutionFrequency } from "utils/constants";
import { getNextValidDateTime } from "utils/shootexecution";
import { minutesByRate } from "utils/transformers";
import { countLeadsFromFile } from "utils/utils";
import { MyFormProps } from ".";

export interface IShootExecutionForm {
  leads_file: File;
  status: ShootExecutionStatus;
  rate_per_minute: number;
  campaign_id: string;
  templates_schedule: INextShootSchedule[];
  negative_cycle_days?: number;
}

export interface IExecutionInfo {
  total_leads: number;
  execution_time: number;
}

interface IFileState {
  value: File | null;
  error: string | null;
  status?: "success" | "error";
}

interface IShootExecutionFormProps extends MyFormProps {
  campaignSelectProps: SelectProps;
  campaigns?: any[];
  onFileChange?: (file: File | null) => void;
}

const StepIndex = ({ label }: { label: string }) => (
  <Col sm={1}>
    <span
      style={{
        fontWeight: "bold",
        width: 20,
        height: 20,
        borderRadius: "50%",
        backgroundColor: "#E2E2E2",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {label}
    </span>
  </Col>
);

const StepSection = ({
  step,
  children,
}: {
  step: string;
  children: React.ReactNode;
}) => (
  <Row>
    <StepIndex label={step} />
    <Col xs={23}>{children}</Col>
  </Row>
);

const buildTemplatesSchedule = (
  templates: { name: string; order: number; curl_key: string }[],
  executionMinutes: number,
  schedule: IScheduleWindow
) => {
  let previous: Dayjs | null = null;
  return templates.map((template) => {
    const scheduled = previous
      ? getNextValidDateTime(previous, executionMinutes, schedule)
      : getNextValidDateTime(dayjs(), 0, schedule);
    previous = scheduled;
    return {
      template_name: template.name,
      order: template.order,
      template_id: template.curl_key,
      scheduled_datetime: scheduled,
    };
  });
};

export function ShootExecutionForm({
  formProps: { onFinish, ...formProps },
  campaignSelectProps,
  campaigns,
  queryResult,
  formLoading,
  onFileChange,
}: IShootExecutionFormProps) {
  const [fileState, setFileState] = useState<IFileState>({
    value: null,
    error: null,
    status: "success",
  });

  const [executionInfo, setExecutionInfo] = useState<IExecutionInfo>({
    total_leads: 0,
    execution_time: 0,
  });

  const { data: scheduleWindow, isLoading } = useQuery<IScheduleWindow>({
    queryKey: ["schedule-window"],
    queryFn: async () => {
      const start = dayjs();
      return await getScheduleWindow({
        start: start.format("YYYY-MM-DD"),
        end: start.add(1, "year").format("YYYY-MM-DD"),
      });
    },
  });

  const debouncedRecalculate = useMemo(
    () =>
      debounce((executionTime: number) => {
        if (!scheduleWindow) return;

        const campaignId = formProps.form?.getFieldValue("campaign_id");
        const selectedCampaign = campaigns?.find((c) => c._id === campaignId);
        const templates = selectedCampaign?.templates || [];

        if (templates.length === 0 || executionTime === 0) return;

        const templates_schedule = buildTemplatesSchedule(
          templates,
          executionTime,
          scheduleWindow
        );

        formProps.form?.setFieldsValue({ templates_schedule });
      }, 400),
    [scheduleWindow, campaigns]
  );

  useEffect(() => {
    return () => debouncedRecalculate.cancel();
  }, [debouncedRecalculate]);

  const updateExecutionInfo = (
    total: number,
    rate: number,
    skipRecalculate?: boolean
  ) => {
    const execution_time = minutesByRate(total, rate);
    const newExecutionInfo = { total_leads: total, execution_time };
    setExecutionInfo(newExecutionInfo);

    if (!skipRecalculate && execution_time > 0) {
      debouncedRecalculate(execution_time);
    }
  };

  const handleFileChange = async (file: File | null) => {
    if (!file) {
      setFileState({
        value: null,
        error: "Base de leads deve ser selecionada.",
        status: "error",
      });

      updateExecutionInfo(0, 0, true);
      onFileChange?.(file);
      return;
    }

    const total = await countLeadsFromFile(file);
    const rate = formProps.form?.getFieldValue("rate_per_minute") || 0;

    updateExecutionInfo(total, rate);
    setFileState({ value: file, error: null, status: "success" });
    onFileChange?.(file);
  };

  const handleFinish = (values: IShootExecutionForm) => {
    if (!fileState.value) {
      return setFileState({
        ...fileState,
        error: "Base de leads deve ser selecionada.",
        status: "error",
      });
    }
    onFinish?.({ ...values, leads_file: fileState.value });
  };

  useEffect(() => {
    const data: ICampaignShoot = queryResult?.data?.data;
    if (!data) return;

    updateExecutionInfo(data.total_leads, data.rate_per_minute, true);

    if (data.file_name)
      setFileState({
        value: new File([], data.file_name),
        error: null,
        status: "success",
      });

    formProps.form?.setFieldsValue({ ...data });
  }, [queryResult?.data?.data]);

  return (
    <Spin size="large" spinning={formLoading || isLoading}>
      <Card>
        <Form
          {...formProps}
          layout="vertical"
          style={{ padding: 16 }}
          onFinish={handleFinish}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {/* Passo 1 */}
            <StepSection step="1">
              <Row gutter={[16, 16]}>
                <Col xs={24}>
                  <Form.Item
                    label="Carregar base de leads"
                    validateStatus={fileState.status}
                    help={fileState.error}
                    required
                  >
                    <CustomUploader
                      hintText="Formato aceito .csv"
                      accept=".csv"
                      value={fileState.value}
                      onChange={handleFileChange}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    name="rate_per_minute"
                    label="Disparo por Minuto"
                    rules={[
                      {
                        required: true,
                        message: "Selecione a frequência de disparo",
                      },
                    ]}
                    getValueFromEvent={(rate) => {
                      updateExecutionInfo(executionInfo.total_leads, rate);
                      return rate;
                    }}
                  >
                    <Select
                      placeholder="Selecione a quantidade de disparo por minuto"
                      allowClear
                    >
                      {shootExecutionFrequency.map((item) => (
                        <Select.Option key={item} value={item}>
                          {item}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <ShootExecutionInfoCard
                    createdAt={formProps.form?.getFieldValue("created_at")}
                    totalLeads={executionInfo.total_leads}
                    executionTime={executionInfo.execution_time}
                  />
                </Col>
              </Row>
            </StepSection>

            {/* Passo 2 */}
            <StepSection step="2">
              <Form.Item
                label="Selecionar a campanha"
                name="campaign_id"
                rules={[{ required: true, message: "Selecione a campanha" }]}
                getValueFromEvent={(value) => {
                  formProps.form?.setFieldsValue({ campaign_id: value });
                  updateExecutionInfo(
                    executionInfo.total_leads,
                    formProps.form?.getFieldValue("rate_per_minute") || 0
                  );
                  return value;
                }}
              >
                <Select
                  {...campaignSelectProps}
                  placeholder="Selecione campanha"
                />
              </Form.Item>
            </StepSection>

            {/* Passo 3 */}
            <StepSection step="3">
              <Space>
                <span style={{ color: "red", fontWeight: "bold" }}>*</span>
                <h4 style={{ fontWeight: "bold" }}>
                  Programar envio dos templates
                </h4>
              </Space>

              <Form.List
                name="templates_schedule"
                rules={[
                  {
                    validator: async (_, value) => {
                      if (!value?.length) {
                        message.error("Selecione pelo menos um template");
                        return Promise.reject();
                      }
                    },
                  },
                ]}
              >
                {(fields) => (
                  <Card
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 16,
                      padding: 16,
                    }}
                  >
                    {fields.map(({ key }, index) => (
                      <div key={key}>
                        <NextShootCard
                          index={index}
                          name={
                            formProps.form?.getFieldValue([
                              "templates_schedule",
                              index,
                              "template_name",
                            ]) || ""
                          }
                          executionMinutes={executionInfo.execution_time}
                          formProps={formProps}
                          schedule={scheduleWindow}
                        />
                        {index < fields.length - 1 && <Divider />}
                      </div>
                    ))}
                  </Card>
                )}
              </Form.List>
            </StepSection>

            {/* Passo 4 */}
            <StepSection step="4">
              <h4 style={{ fontWeight: "bold" }}>Pausar próximo disparo</h4>
              <Form.Item
                name="negative_cycle_days"
                label="Tempo para o próximo engajamento"
              >
                <InputNumber
                  defaultValue={10}
                  placeholder="Digite a quantidade em dias"
                  style={{ width: "100%" }}
                  min={0}
                  max={120}
                />
              </Form.Item>
            </StepSection>
          </div>
        </Form>
      </Card>
    </Spin>
  );
}
